import { useState, useMemo, useCallback, useEffect } from 'react';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/app/components/ui/dialog';
import { cn } from '@/app/components/ui/utils';
import { usePlatformModules } from '@/hooks/use-modules';
import { getModuleDisplayName } from '@/lib/modules';
import {
  useAllModuleRoles,
  useCreateModuleRole,
  useUpdateModuleRole,
  useBulkSetModuleRoles,
} from '@/hooks/use-admin';
import { useUsers } from '@/hooks/use-settings';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import type { ModuleRoleLevel } from '@/lib/types';

const roleColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800 border-purple-200',
  manager: 'bg-blue-100 text-blue-800 border-blue-200',
  supervisor: 'bg-teal-100 text-teal-800 border-teal-200',
  user: 'bg-green-100 text-green-800 border-green-200',
  viewer: 'bg-gray-100 text-gray-800 border-gray-200',
  no_access: 'bg-white text-gray-400 border-gray-200',
};

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  supervisor: 'Supervisor',
  user: 'User',
  viewer: 'Viewer',
  no_access: 'No Access',
};

interface GridUser {
  userId: number;
  userName: string;
  userEmail: string;
}

export function PermissionsTab() {
  const [selectedPeople, setSelectedPeople] = useState<number[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { enabledModules } = usePlatformModules();
  const { data: rolesData, isLoading: rolesLoading, isError: rolesError } = useAllModuleRoles();
  const { data: usersData, isLoading: usersLoading } = useUsers();

  const isLoading = rolesLoading || usersLoading;

  // Always include 'assets' (H3Gear) so the tab is useful even in single-module orgs.
  const displayModules = enabledModules.includes('assets')
    ? enabledModules
    : ['assets', ...enabledModules];

  useEffect(() => {
    if (rolesError) {
      toast.error('Failed to load permissions');
    }
  }, [rolesError]);

  const createModuleRole = useCreateModuleRole();
  const updateModuleRole = useUpdateModuleRole();

  // Build users list from all org members so the grid shows everyone,
  // including those who don't yet have any module roles assigned.
  const gridUsers = useMemo((): GridUser[] => {
    if (!usersData?.results) return [];
    return usersData.results.map((u) => ({
      userId: u.id,
      userName: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username,
      userEmail: u.email || '',
    }));
  }, [usersData]);

  // Build a lookup: userId -> { module -> ModuleRole }
  const rolesMap = useMemo(() => {
    const map: Record<number, Record<string, { id: number; role: ModuleRoleLevel }>> = {};
    if (rolesData?.results) {
      for (const mr of rolesData.results) {
        if (!map[mr.user]) map[mr.user] = {};
        map[mr.user][mr.module] = { id: mr.id, role: mr.role };
      }
    }
    return map;
  }, [rolesData]);

  const handleRoleChange = useCallback(async (
    user: GridUser,
    module: string,
    newRole: ModuleRoleLevel
  ) => {
    try {
      const existing = rolesMap[user.userId]?.[module];
      if (existing) {
        await updateModuleRole.mutateAsync({ id: existing.id, data: { role: newRole } });
      } else {
        const response = await createModuleRole.mutateAsync({
          user: user.userId,
          module,
          role: newRole,
        });
        if (response.mfa_warning) {
          toast.warning(response.mfa_warning, { duration: 10000 });
        }
      }
    } catch {
      toast.error('Failed to update permission');
    }
  }, [rolesMap, updateModuleRole, createModuleRole]);

  const toggleSelectPerson = (userId: number) => {
    setSelectedPeople((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (gridUsers.length === 0) return;
    const allUserIds = gridUsers.map((u) => u.userId);
    if (selectedPeople.length === allUserIds.length) {
      setSelectedPeople([]);
    } else {
      setSelectedPeople(allUserIds);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-gray-600">
          Manage per-module permissions for all organization members.
        </p>
        {selectedPeople.length > 0 && (
          <button
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Bulk Set ({selectedPeople.length} selected)
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(roleColors).map(([role, colorClass]) => (
          <span
            key={role}
            className={cn('inline-flex items-center px-2 py-0.5 text-xs rounded border', colorClass)}
          >
            {roleLabels[role]}
          </span>
        ))}
      </div>

      {/* Matrix */}
      {/* Desktop table */}
      <div className="hidden lg:block border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-8">
                  <input
                    type="checkbox"
                    checked={gridUsers.length > 0 && selectedPeople.length === gridUsers.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 min-w-[200px]">
                  Member
                </th>
                {displayModules.map((mod) => (
                  <th
                    key={mod}
                    className="text-center px-4 py-3 font-medium text-gray-600 min-w-[140px]"
                  >
                    {getModuleDisplayName(mod)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-4" /></td>
                    <td className="px-4 py-3">
                      <div className="space-y-1.5">
                        <div className="h-4 bg-gray-200 rounded w-32" />
                        <div className="h-3 bg-gray-100 rounded w-40" />
                      </div>
                    </td>
                    {displayModules.map((mod) => (
                      <td key={mod} className="px-2 py-3 text-center">
                        <div className="h-8 bg-gray-200 rounded w-[120px] mx-auto" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : gridUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={2 + displayModules.length}
                    className="px-4 py-16 text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                        <Shield className="h-6 w-6 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">No permissions to manage</p>
                        <p className="text-sm text-gray-500 mt-1">Invite team members first to manage permissions.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                gridUsers.map((user) => (
                  <tr key={user.userId} className="border-b hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedPeople.includes(user.userId)}
                        onChange={() => toggleSelectPerson(user.userId)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {user.userName}
                        </span>
                        <Badge className="text-xs bg-gray-100 text-gray-800">
                          Member
                        </Badge>
                      </div>
                      <span className="text-xs text-gray-500">{user.userEmail}</span>
                    </td>
                    {displayModules.map((mod) => {
                      const currentRole = rolesMap[user.userId]?.[mod]?.role || 'no_access';
                      return (
                        <td key={mod} className="px-2 py-3 text-center">
                          <Select
                            value={currentRole}
                            onValueChange={(v) =>
                              handleRoleChange(user, mod, v as ModuleRoleLevel)
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                'w-[120px] mx-auto text-xs border',
                                roleColors[currentRole]
                              )}
                              size="sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="supervisor">Supervisor</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="no_access">No Access</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="lg:hidden space-y-3">
        {!isLoading && gridUsers.length > 0 && (
          <label className="flex items-center gap-2 min-h-[44px] px-1">
            <input
              type="checkbox"
              checked={gridUsers.length > 0 && selectedPeople.length === gridUsers.length}
              onChange={toggleSelectAll}
              className="rounded border-gray-300 min-w-[20px] min-h-[20px]"
            />
            <span className="text-sm text-gray-600">Select all</span>
          </label>
        )}
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-32" />
                <div className="h-3 bg-gray-100 rounded w-40" />
                <div className="h-8 bg-gray-200 rounded w-full" />
              </CardContent>
            </Card>
          ))
        ) : gridUsers.length === 0 ? (
          <div className="py-16 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                <Shield className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">No permissions to manage</p>
                <p className="text-sm text-gray-500 mt-1">Invite team members first to manage permissions.</p>
              </div>
            </div>
          </div>
        ) : (
          gridUsers.map((user) => (
            <Card key={user.userId}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <input
                    type="checkbox"
                    checked={selectedPeople.includes(user.userId)}
                    onChange={() => toggleSelectPerson(user.userId)}
                    className="rounded border-gray-300 mt-1 min-w-[20px] min-h-[20px]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{user.userName}</span>
                      <Badge className="text-xs bg-gray-100 text-gray-800">Member</Badge>
                    </div>
                    <span className="text-xs text-gray-500">{user.userEmail}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {displayModules.map((mod) => {
                    const currentRole = rolesMap[user.userId]?.[mod]?.role || 'no_access';
                    return (
                      <div key={mod} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-700 flex-shrink-0">
                          {getModuleDisplayName(mod)}
                        </span>
                        <Select
                          value={currentRole}
                          onValueChange={(v) =>
                            handleRoleChange(user, mod, v as ModuleRoleLevel)
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              'w-full max-w-[180px] min-h-[44px] text-xs border',
                              roleColors[currentRole]
                            )}
                            size="sm"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="supervisor">Supervisor</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="no_access">No Access</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Bulk Set Modal */}
      <BulkSetModal
        open={bulkOpen}
        onOpenChange={(open) => {
          setBulkOpen(open);
          if (!open) setSelectedPeople([]);
        }}
        selectedUserIds={selectedPeople}
        enabledModules={displayModules}
      />
    </div>
  );
}

// ─── Bulk Set Modal ─────────────────────────────────────────────────────────

function BulkSetModal({
  open,
  onOpenChange,
  selectedUserIds,
  enabledModules,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: number[];
  enabledModules: string[];
}) {
  const [selectedModule, setSelectedModule] = useState<string>(enabledModules[0] || '');
  const [selectedRole, setSelectedRole] = useState<ModuleRoleLevel>('viewer');

  const bulkSetMutation = useBulkSetModuleRoles();

  const handleSubmit = async () => {
    if (!selectedModule) return;
    await bulkSetMutation.mutateAsync({
      user_ids: selectedUserIds,
      module: selectedModule,
      role: selectedRole,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Bulk Set Role</DialogTitle>
          <DialogDescription>
            Set the same role for {selectedUserIds.length} selected
            {selectedUserIds.length === 1 ? ' member' : ' members'}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div data-field-container>
            <label className="block text-sm font-medium text-gray-700 mb-1">Module *</label>
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger
                size="sm"
                data-required="true"
                data-required-empty={selectedModule ? 'false' : 'true'}
                data-required-message="Module is required."
              >
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent>
                {enabledModules.map((mod) => (
                  <SelectItem key={mod} value={mod}>
                    {getModuleDisplayName(mod)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as ModuleRoleLevel)}>
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="no_access">No Access</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            data-validate-required="true"
            disabled={bulkSetMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {bulkSetMutation.isPending ? 'Applying...' : 'Apply'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
